import { useEffect, useMemo, useRef, useState } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { APIError } from "@/lib/api";
import { billingCycleKeys } from "@/hooks/useBillingCycleQueries";
import {
  adminResourceService,
  type MetricsQueryParams,
} from "@/services/admin-resource.service";
import { adminBillingCycleService } from "@/services/admin-billing-cycle.service";
import type { UpdateBillingCycleRequest } from "@/types/billing-cycle";
import type {
  ResourceFilters,
  ResourceActionListFilters,
  ResourceReinstallListFilters,
  ResourceReinstallReportFilters,
  ResourceSnapshot,
  N8nEndpoint,
  N8nProbeResult,
  ResourceConfigChangeQuote,
  ResourceConfigChangeLimits,
  ResourceConfigChangeExecution,
  ResourceConfigChangeTarget,
  UpdateByUuidPreview,
  UpdateByUuidExecution,
  BulkStopResourcesPayload,
  ExpiredVmFilters,
  DeleteExpiredVmsPayload,
  OrphanChildrenFilters,
  ResourceProviderState,
  RemoveOrphanChildrenPayload,
  ResourceBackfillField,
  DeleteResourcePayload,
  ReinstallResourcePayload,
  ResourceStatus,
  ResourceStatusSnapshot,
  ApplyVmIpAddressPayload,
  FirewallRulesResponse,
  FirewallLog,
  FirewallMacro,
  ProvisioningRetryCheck,
  RetryProvisioningRequest,
  Resource,
  ResourceDetail,
} from "@/types/admin";
import type { PaginationInfo } from "@/types/api-response";
import {
  RESOURCE_POWER_ACTION_META,
  type ResourcePowerAction,
} from "@/lib/resourcePowerActions";
import { describeRetryOutcome } from "@/lib/retryProvisioning";
import { awaitRefresh } from "@/lib/awaitRefresh";
import { formatVietnamDateTime } from "@/lib/date-utils";

/**
 * Query keys for resource-related queries
 */
export const resourceKeys = {
  all: ["resources"] as const,
  lists: () => [...resourceKeys.all, "list"] as const,
  list: (filters: ResourceFilters) =>
    [...resourceKeys.lists(), filters] as const,
  details: () => [...resourceKeys.all, "detail"] as const,
  detail: (id: string) => [...resourceKeys.details(), id] as const,
  actions: (id: string) => [...resourceKeys.detail(id), "actions"] as const,
  // Keyed by its whole filter set (the detail page's tab passes resourceId, page and page size), not
  // nested under detail(id): the endpoint is not resource-scoped by route, and the older
  // `actions(id)` key above still belongs to the unpaginated per-resource endpoint.
  actionLists: () => [...resourceKeys.all, "action-list"] as const,
  actionList: (filters: ResourceActionListFilters) =>
    [...resourceKeys.actionLists(), filters] as const,
  reinstallLists: () => [...resourceKeys.all, "reinstall-list"] as const,
  reinstallList: (filters: ResourceReinstallListFilters) =>
    [...resourceKeys.reinstallLists(), filters] as const,
  reinstallReports: () => [...resourceKeys.all, "reinstall-report"] as const,
  reinstallReport: (filters: ResourceReinstallReportFilters) =>
    [...resourceKeys.reinstallReports(), filters] as const,
  activityLog: (id: string) =>
    [...resourceKeys.detail(id), "activity-log"] as const,
  // Keyed by the stable range/interval, NOT literal from/to timestamps — those are computed fresh
  // inside the queryFn on every fetch (initial + each refetchInterval tick) instead of at render
  // time, so the query identity stays stable and refetchInterval can actually keep the chart live.
  metrics: (
    id: string,
    rangeKey: string,
    interval?: MetricsQueryParams["interval"],
  ) => [...resourceKeys.detail(id), "metrics", rangeKey, interval] as const,
  n8nEndpoints: () => [...resourceKeys.all, "n8n", "endpoints"] as const,
  n8nProbe: (id: string, endpointKey: string, timeframe?: string) =>
    [
      ...resourceKeys.detail(id),
      "n8n",
      endpointKey,
      timeframe ?? null,
    ] as const,
  configChangeLimits: (id: string) =>
    [...resourceKeys.detail(id), "config-change-limits"] as const,
  attachments: (id: string) =>
    [...resourceKeys.detail(id), "attachments"] as const,
  snapshots: (id: string) => [...resourceKeys.detail(id), "snapshots"] as const,
  firewall: (id: string) => [...resourceKeys.detail(id), "firewall"] as const,
  firewallLogs: (id: string) =>
    [...resourceKeys.detail(id), "firewall", "logs"] as const,
  // One entry per watched id set, sorted by the caller so the same set is the same query.
  statuses: (sortedIds: string[]) =>
    [...resourceKeys.all, "statuses", ...sortedIds] as const,
  // Not resource-scoped — the macro catalog is the same for every VM.
  firewallMacros: () => [...resourceKeys.all, "firewall-macros"] as const,
  // One entry per (sorted) id set the caller batched — a detail page sends one id, a list page
  // sends every Error VM row on its current page.
  provisioningRetryChecks: () =>
    [...resourceKeys.all, "provisioning-retry-check"] as const,
  provisioningRetryCheck: (sortedIds: string[]) =>
    [...resourceKeys.provisioningRetryChecks(), ...sortedIds] as const,
};

/**
 * Hook to fetch paginated resource list with filters
 * Returns: { data: Resource[]; pagination: PaginationInfo }
 */
export function useResources(filters: ResourceFilters) {
  return useQuery({
    queryKey: resourceKeys.list(filters),
    queryFn: () => adminResourceService.getResources(filters),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // keep the table visible while a new page/filter loads
    // Terminating rows are not polled here: `useResourceStatusWatch` reads just their statuses and
    // refetches the list when one moves.
  });
}

/**
 * Hook to fetch resource details
 * Returns: ResourceDetail
 */
export function useResourceDetail(id: string) {
  return useQuery({
    queryKey: resourceKeys.detail(id),
    queryFn: () => adminResourceService.getResourceById(id),
    enabled: !!id,
    staleTime: 60000, // 1 minute
  });
}

/** How often the resource list re-reads the statuses of the Terminating rows on its page. */
export const LIST_STATUS_WATCH_MS = 60_000;

/** How often a Terminating resource's detail page re-reads its status. */
export const DETAIL_STATUS_WATCH_MS = 15_000;

/**
 * How often the expired-VM clean-up re-reads the statuses of its Terminating rows: at most ten ids
 * from the database, and the operator is watching the deletions they just started.
 */
export const EXPIRED_VMS_STATUS_WATCH_MS = 15_000;

/**
 * How long one set of ids is watched before the watch gives up. n8n usually answers within a
 * minute; a deletion still Terminating after this is stuck, and polling it forever only costs
 * requests. A new set of ids (a row joins or leaves) starts a new window.
 */
export const STATUS_WATCH_CAP_MS = 15 * 60_000;

/**
 * How long a set that includes a live reinstall is watched before the watch gives up. Longer than
 * the plain deletion cap: `ReinstallVmJob` itself waits up to 10 minutes, and a lost chain is only
 * parked at `Error` by `ReinstallRecoveryJob` after its own `StuckAfter` (15 min) plus its scan
 * cadence (every 10 min) plus another 10-minute wait — up to ~35 minutes end to end. 15 minutes
 * would stop refreshing well before that and leave the badge reading "Reinstalling" forever.
 */
export const REINSTALL_STATUS_WATCH_CAP_MS = 40 * 60_000;

/**
 * How a watched resource settled, as far as this console reacts to it. The first two are a
 * Terminating row leaving deletion; the next three are a live `Decommissioning` row (a reinstall
 * in flight, `deletedAt` null) leaving it — see `useResourceStatusWatch`'s `reinstallingIds`.
 */
type ResourceOutcomeKind =
  | "Deleted"
  | "DeletionFailed"
  | "Changed"
  | "Reinstalled"
  | "ReinstallRefused"
  | "ReinstallErrored";

/**
 * Outcomes already acted on, by resource id. Module-wide so every watcher — and the socket listener
 * once the admin socket is wired — toasts and invalidates once per outcome, however many polls or
 * events report it. An id is forgotten when it is seen Terminating again: a new deletion attempt.
 */
const settledOutcomes = new Map<string, ResourceOutcomeKind>();

/** Forgets an acted-on outcome, so the next deletion attempt of this resource reports its own. */
export function forgetResourceOutcome(id: string): void {
  settledOutcomes.delete(id);
}

/**
 * What a status read says about a watched resource: null while it is still on its way (Terminating
 * for a deletion, live `Decommissioning` for a reinstall). An absent snapshot means the row no
 * longer exists at all (a record-only removal).
 *
 * `isReinstalling` tells apart the two watches, which is needed here: `Decommissioning` with
 * `deletedAt` null is itself the "still in flight" reading for a reinstall, and a reinstall's three
 * outcomes (`Running` done, `Stopped` refused, `Error` never booted) are statuses a Terminating row
 * never settles as on its own (§5a, §5 `ReinstallVmJob` table).
 */
function readResourceOutcome(
  snapshot: ResourceStatusSnapshot | undefined,
  isReinstalling: boolean,
): ResourceOutcomeKind | null {
  if (!snapshot) {
    return "Changed";
  }

  if (isReinstalling) {
    if (snapshot.status === "Decommissioning" && snapshot.deletedAt === null) {
      return null;
    }

    if (snapshot.status === "Running") {
      return "Reinstalled";
    }

    if (snapshot.status === "Stopped") {
      return "ReinstallRefused";
    }

    if (snapshot.status === "Error") {
      return "ReinstallErrored";
    }

    // An operator's manual status override mid-reinstall (Change status) — out of scope (design
    // doc §14 D11): refresh quietly rather than guess at an outcome.
    return "Changed";
  }

  if (snapshot.status === "Terminating") {
    return null;
  }

  if (snapshot.status === "Decommissioning" && snapshot.deletedAt) {
    return "Deleted";
  }

  if (snapshot.deletionFailedAt) {
    return "DeletionFailed";
  }

  return "Changed";
}

/**
 * Acts once on a resource leaving Terminating: refetches the lists, drops (deleted) or refetches
 * (anything else) its detail, and toasts a VM's deletion outcome. Returns false, doing nothing,
 * when this outcome was already acted on. Only a VM toasts: its disks and networks move with it
 * and would repeat the message.
 *
 * `invalidateLists` defaults to true for a call outside a batch (an external caller settling one
 * resource on its own, e.g. a future socket listener). `useResourceStatusWatch` passes false and
 * invalidates the lists itself, once, after every id in the read has been settled — see there.
 */
export function settleResourceOutcome(
  queryClient: QueryClient,
  resource: { id: string; name: string | null; isVm: boolean },
  kind: ResourceOutcomeKind,
  restorableUntil: string | null = null,
  invalidateLists: boolean = true,
): boolean {
  if (settledOutcomes.get(resource.id) === kind) {
    return false;
  }

  settledOutcomes.set(resource.id, kind);
  if (invalidateLists) {
    void queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
  }

  if (kind === "Deleted") {
    queryClient.removeQueries({ queryKey: resourceKeys.detail(resource.id) });
  } else {
    // Exact: the detail's children (metrics, snapshots, firewall) each ask the provider.
    void queryClient.invalidateQueries({
      queryKey: resourceKeys.detail(resource.id),
      exact: true,
    });
    // A settled outcome (deletion refused, reinstall finished/refused/errored) is itself audited,
    // so the detail page's activity tab must catch up the same moment the header does.
    void queryClient.invalidateQueries({
      queryKey: resourceKeys.activityLog(resource.id),
    });
  }

  if (!resource.isVm) {
    return true;
  }

  if (kind === "Deleted") {
    const until = restorableUntil
      ? ` Kept until ${formatVietnamDateTime(restorableUntil)}, then deleted for good.`
      : "";
    toast.success(`VM ${resource.name} deleted.${until}`);
  } else if (kind === "DeletionFailed") {
    toast.error(`Deletion of VM ${resource.name} did not complete`, {
      description:
        "The infrastructure refused it; the VM is unchanged. Try again or check the infrastructure.",
    });
  } else if (kind === "Reinstalled") {
    toast.success("Reinstall finished", {
      description: `${resource.name} is running again.`,
    });
  } else if (kind === "ReinstallRefused") {
    toast.error(`Reinstall of VM ${resource.name} was refused`, {
      description:
        "The infrastructure refused the reinstall; the VM is unchanged.",
    });
  } else if (kind === "ReinstallErrored") {
    toast.error(`Reinstall of VM ${resource.name} failed`, {
      description: "The VM did not come back after the reinstall.",
    });
  }

  return true;
}

/**
 * Watches resources that are Terminating (deleting) or live `Decommissioning` (reinstalling) by
 * reading only their statuses (`GET /admin/resources/statuses`) every `intervalMs`, and reacts once
 * when one settles: see `settleResourceOutcome`. An unchanged read touches nothing else. Polls only
 * while the tab is visible, and stops `STATUS_WATCH_CAP_MS` after the id set was first watched.
 * `onDeleted` runs whenever a watched id is read as deleted (the detail page leaves on it).
 *
 * `reinstallingIds` — a subset of `ids` — tells `readResourceOutcome` which ones to read as a live
 * reinstall (ending on `Running`/`Stopped`/`Error`) instead of a deletion (ending on `Decommissioning`
 * with `deletedAt`, or a reverted status with `deletionFailedAt`). Defaults to none, so a caller that
 * only ever watches Terminating rows is unaffected. A non-empty set also switches the watch's own
 * cap to `REINSTALL_STATUS_WATCH_CAP_MS` (see there for why plain deletions keep the shorter one).
 *
 * Returns `isWatchCapExpired` (true once the current id set has been watched past its cap, with
 * nothing settled yet) and `resumeWatch` (start a fresh window for the same ids and refetch once
 * immediately), so a caller like the detail page can tell the operator refreshing stopped and offer
 * a way to restart it instead of leaving a status badge spinning silently forever.
 */
export function useResourceStatusWatch(
  ids: string[],
  intervalMs: number,
  onDeleted?: (id: string) => void,
  reinstallingIds: string[] = [],
): { isWatchCapExpired: boolean; resumeWatch: () => void } {
  const queryClient = useQueryClient();
  const idsKey = [...ids].sort().join(",");
  const sortedIds = useMemo(
    () => (idsKey.length > 0 ? idsKey.split(",") : []),
    [idsKey],
  );
  const reinstallingKey = [...reinstallingIds].sort().join(",");
  const reinstallingSet = useMemo(
    () => new Set(reinstallingKey.length > 0 ? reinstallingKey.split(",") : []),
    [reinstallingKey],
  );
  const capMs =
    reinstallingSet.size > 0
      ? REINSTALL_STATUS_WATCH_CAP_MS
      : STATUS_WATCH_CAP_MS;
  const watchStart = useRef<{ idsKey: string; at: number } | null>(null);
  const onDeletedRef = useRef(onDeleted);
  const [isWatchCapExpired, setIsWatchCapExpired] = useState(false);
  // Bumped by `resumeWatch` to force the cap timer below to restart without the id set itself
  // changing.
  const [resumeNonce, setResumeNonce] = useState(0);

  useEffect(() => {
    onDeletedRef.current = onDeleted;
  }, [onDeleted]);

  const { data: snapshots, refetch: refetchStatuses } = useQuery({
    queryKey: resourceKeys.statuses(sortedIds),
    queryFn: () => adminResourceService.getStatuses(sortedIds),
    enabled: sortedIds.length > 0,
    // Never served from cache to a later watch of the same set: an old "Terminating" read would
    // forget an outcome already reported and report it again.
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: () => {
      const start = watchStart.current;
      if (start === null || start.idsKey !== idsKey) {
        watchStart.current = { idsKey, at: Date.now() };
        return intervalMs;
      }

      const withinCap = Date.now() - start.at < capMs;
      return withinCap ? intervalMs : false;
    },
  });

  // Mirrors the cap the interval above enforces, as a piece of render state a caller can act on.
  // Keyed on the same identity (the id set, the reinstalling subset, and an explicit resume) as the
  // window the interval itself tracks, so the two never disagree about when a window started.
  useEffect(() => {
    if (sortedIds.length === 0) {
      setIsWatchCapExpired(false);
      return;
    }

    setIsWatchCapExpired(false);
    const timer = window.setTimeout(() => setIsWatchCapExpired(true), capMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reinstallingKey, capMs, resumeNonce]);

  const resumeWatch = () => {
    // Nulled so the next `refetchInterval` call reseeds it with `Date.now()`, as if this id set had
    // just started being watched.
    watchStart.current = null;
    setIsWatchCapExpired(false);
    setResumeNonce((n) => n + 1);
    // refetch() runs even while the query is disabled, and an empty id list is a 400.
    if (sortedIds.length > 0) {
      void refetchStatuses();
    }
  };

  useEffect(() => {
    if (!snapshots) {
      return;
    }

    // Gathered across every id in this one read, so a read that moves several resources at once
    // (several VMs finishing deletion in the same tick) invalidates the lists once, not once per
    // id — the same list refetch answers for all of them.
    let anySettled = false;
    for (const id of sortedIds) {
      const snapshot = snapshots[id];
      const kind = readResourceOutcome(snapshot, reinstallingSet.has(id));
      if (kind === null) {
        forgetResourceOutcome(id);
        continue;
      }

      if (kind === "Deleted") {
        onDeletedRef.current?.(id);
      }

      const settled = settleResourceOutcome(
        queryClient,
        {
          id,
          name: snapshot?.name ?? null,
          isVm: snapshot?.type === "VirtualMachine",
        },
        kind,
        snapshot?.restorableUntil ?? null,
        false,
      );
      anySettled = anySettled || settled;
    }

    if (anySettled) {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      // The expired-VM clean-up lists Terminating rows too: the settled one leaves it or shows its
      // status back. Its provider states are not invalidated; a changed page reads its id set again.
      void queryClient.invalidateQueries({ queryKey: expiredVmKeys.all });
    }
  }, [snapshots, sortedIds, reinstallingSet, queryClient]);

  return { isWatchCapExpired, resumeWatch };
}

/**
 * Hook to fetch resource actions
 * Returns: ResourceAction[]
 */
export function useResourceActions(id: string) {
  return useQuery({
    queryKey: resourceKeys.actions(id),
    queryFn: () => adminResourceService.getResourceActions(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch one page of the resource action trail (SuperAdmin, SupportAdmin). The resource
 * detail page's "Resource Actions" tab passes `resourceId`; every other filter is optional.
 *
 * `placeholderData` keeps the previous page on screen across a filter or page change, matching
 * every other admin list; `staleTime` is short because this trail is written by whichever
 * operator or job just ran an action, not something an operator edits from this screen.
 * Returns: { data: ResourceActionListItem[]; pagination: PaginationInfo }
 */
export function useResourceActionList(filters: ResourceActionListFilters) {
  return useQuery({
    queryKey: resourceKeys.actionList(filters),
    queryFn: () => adminResourceService.getResourceActionList(filters),
    staleTime: 10000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch one page of the VM-reinstall report (SuperAdmin, SupportAdmin).
 * `placeholderData` keeps the previous page on screen across a filter or page change.
 * Returns: { data: ResourceReinstallListItem[]; pagination: PaginationInfo }
 */
export function useResourceReinstalls(filters: ResourceReinstallListFilters) {
  return useQuery({
    queryKey: resourceKeys.reinstallList(filters),
    queryFn: () => adminResourceService.getResourceReinstalls(filters),
    staleTime: 10000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch the VM-reinstall report for a window (SuperAdmin, SupportAdmin).
 * `placeholderData` keeps the previous figures on screen while a new window or filter loads.
 * Returns: ResourceReinstallReport
 */
export function useResourceReinstallReport(
  filters: ResourceReinstallReportFilters,
) {
  return useQuery({
    queryKey: resourceKeys.reinstallReport(filters),
    queryFn: () => adminResourceService.getResourceReinstallReport(filters),
    staleTime: 10000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to remove one row of the VM-reinstall log (SuperAdmin only). The log and the report are
 * re-read: the row is not shown on any other screen.
 */
export function useDeleteResourceReinstall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      adminResourceService.deleteResourceReinstall(id),
    onSuccess: () => {
      toast.success("Reinstall record removed", {
        description: "It is no longer counted against the reinstall limit.",
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to remove the reinstall record", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // The removed row leaves the log and every figure of the report computed from it.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: resourceKeys.reinstallReports(),
      });
      return queryClient.invalidateQueries({
        queryKey: resourceKeys.reinstallLists(),
      });
    },
  });
}

/**
 * Hook to fetch paginated audit log activity for a resource. Fixed at page 1 / 50 items — the
 * caller doesn't expose pagination controls yet, matching the client app's fixed page size.
 * Returns: { data: ResourceActivityLogEntry[]; pagination: PaginationInfo }
 */
export function useResourceActivityLog(id: string) {
  return useQuery({
    queryKey: resourceKeys.activityLog(id),
    queryFn: () => adminResourceService.getResourceActivityLog(id, 1, 50),
    enabled: !!id,
  });
}

/**
 * Polling cadence for the resource-detail metric charts (Overview and Metrics tabs). Both
 * queries are declared at page level, so the tab gate on `enabled` — not unmounting — is what
 * keeps the inactive tab silent. Only windows up to an hour poll; see `useResourceMetrics`.
 */
export const RESOURCE_METRICS_POLL_MS = 60000;

const MAX_POLLED_WINDOW_MS = 60 * 60 * 1000;

/**
 * Window spec for {@link useResourceMetrics}. `rangeKey` is a stable label used only for the query
 * key (e.g. "1h", "24h", "7d") — the actual from/to timestamps are derived from `windowMs`
 * fresh at fetch time, not baked in at render time.
 */
export interface MetricsWindow {
  windowMs: number;
  interval?: MetricsQueryParams["interval"];
  rangeKey: string;
}

/**
 * Hook to fetch resource metrics for a rolling window ending "now". Mirrors the client app's
 * pattern (stable queryKey, `now` resolved inside queryFn) so `refetchInterval` refreshes the
 * SAME query in place — computing `from`/`to` from `new Date()` in the render body instead would
 * change the query key on every render (any state update anywhere on the page), spawning a brand
 * new query each time instead of ticking the existing one, which is why the chart looked frozen.
 * Returns: ResourceMetricsSeries
 */
export function useResourceMetrics(
  id: string,
  window: MetricsWindow,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: resourceKeys.metrics(id, window.rangeKey, window.interval),
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - window.windowMs);
      return adminResourceService.getResourceMetrics(id, {
        from: from.toISOString(),
        to: to.toISOString(),
        interval: window.interval,
      });
    },
    enabled: !!id && enabled,
    // One refresh a minute, and only for the tab the operator is actually on — `enabled` is
    // gated on that at the call site, and a disabled query never ticks. Longer windows are coarse
    // samples (30 minutes apart at 24h, days at 1Y), so polling them re-asked the provider for a
    // series that had not changed.
    refetchInterval:
      window.windowMs <= MAX_POLLED_WINDOW_MS
        ? RESOURCE_METRICS_POLL_MS
        : false,
    // As on the client: keep the previous series while a new range loads, instead of blanking
    // every chart back to "Loading…".
    staleTime: 10000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch the WiService (n8n) probe catalog. The list is static per deployment, so it is
 * cached for the session rather than refetched per tab visit.
 * Returns: N8nEndpoint[]
 */
export function useResourceN8nEndpoints(enabled: boolean = true) {
  return useQuery<N8nEndpoint[]>({
    queryKey: resourceKeys.n8nEndpoints(),
    queryFn: () => adminResourceService.getN8nEndpoints(),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * Hook to run one n8n probe. Every call is a live round trip to the provider, so nothing is served
 * from cache (`staleTime: 0`, `gcTime: 0`) and nothing is retried — a probe that failed is itself
 * the observation, and a silent retry would misreport how the provider answered.
 * Returns: N8nProbeResult
 */
export function useResourceN8nProbe(
  id: string,
  endpointKey: string | null,
  timeframe?: string,
  enabled: boolean = true,
) {
  return useQuery<N8nProbeResult>({
    queryKey: resourceKeys.n8nProbe(id, endpointKey ?? "", timeframe),
    queryFn: () =>
      adminResourceService.getN8nProbe(id, endpointKey!, timeframe),
    enabled: !!id && !!endpointKey && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/*
 * Loading standard for the resource writes below.
 *
 * A write invalidates from `onSettled` and RETURNS the promise. React Query awaits that callback
 * before `isPending` clears, and a caller's own `mutate(…, { onSuccess })` — the one that closes a
 * dialog or a menu — runs only after it. So the spinner, the held-open dialog and the page overlay
 * lift together with the new state on screen rather than on the bare API answer, which used to leave
 * a freshly stopped VM reading "Running" under a closed menu with every action enabled again. It runs
 * on failure too: a provider timeout does not prove nothing changed.
 *
 * The one deliberate exception is `useApplyVmIpAddress`, whose answer can carry a generated password
 * — see its own note.
 */

/**
 * Hook for the range a VM's configuration can be changed within (SuperAdmin only): the vCPU,
 * memory and disk range of its product line, which the dialog draws its controls from. It reads no
 * provider, unlike the quote below, so it is an ordinary query.
 * Returns: ResourceConfigChangeLimits
 */
export function useConfigChangeLimits(id: string, enabled: boolean = true) {
  return useQuery<ResourceConfigChangeLimits>({
    queryKey: resourceKeys.configChangeLimits(id),
    queryFn: () => adminResourceService.getConfigChangeLimits(id),
    enabled: !!id && enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to price a configuration change without applying anything (SuperAdmin only).
 *
 * A mutation rather than a query: the target configuration is a body, and re-pricing is something
 * the operator triggers by editing the form, not something that should refetch on its own. Each
 * call is a live round trip to the infrastructure, so the answer carries its own `checkedAt`.
 */
export function useConfigChangeQuote() {
  return useMutation<
    ResourceConfigChangeQuote,
    Error,
    { id: string; target: ResourceConfigChangeTarget }
  >({
    mutationFn: ({ id, target }) =>
      adminResourceService.quoteConfigChange(id, target),
    retry: false,
  });
}

/**
 * Hook to change a VM's configuration end to end on one confirmation (SuperAdmin only).
 *
 * This is what the dialog calls. It applies the change, corrects the record and moves the price,
 * so it is the mutation that invalidates: the resource's configuration, its monthly price and its
 * open billing cycle can all have moved.
 *
 * Raises no toast on purpose — the dialog shows the outcome of every block in place, and a partial
 * result is something to read rather than something to flash past.
 */
export function useExecuteConfigChange() {
  const queryClient = useQueryClient();

  return useMutation<
    ResourceConfigChangeExecution,
    Error,
    { id: string; target: ResourceConfigChangeTarget }
  >({
    mutationFn: ({ id, target }) =>
      adminResourceService.executeConfigChange(id, target),
    retry: false,
    // Invalidated one by one rather than by the `all` prefix: that prefix also covers the n8n probe
    // keys, and invalidating them would send open panels back to the provider.
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.actions(id) }),
          // The detail page's "Resource Actions" tab reads the same trail under its own filter key.
          queryClient.invalidateQueries({
            queryKey: resourceKeys.actionLists(),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      ),
  });
}

/**
 * Checks whether a VM whose provisioning never finished is really on the infrastructure, and what
 * recovering it would do. A mutation rather than a query on purpose: it makes a live outbound call
 * to a webhook the operational notes describe as slow, so it fires when an operator asks and never
 * on its own.
 * Returns: UpdateByUuidPreview
 */
export function usePreviewUpdateByUuid() {
  return useMutation<
    UpdateByUuidPreview,
    Error,
    { id: string; vmUuid?: string }
  >({
    mutationFn: ({ id, vmUuid }) =>
      adminResourceService.previewUpdateByUuid(id, vmUuid),
    retry: false,
  });
}

/**
 * Recovers a VM from the infrastructure end to end.
 *
 * Raises no toast on purpose — the recovery page reports the outcome in place, and a run that
 * stopped part-way is something to read rather than something to flash past.
 */
export function useUpdateResourceByUuid() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateByUuidExecution,
    Error,
    { id: string; vmUuid?: string; poolName?: string; osTemplateId?: string }
  >({
    mutationFn: ({ id, vmUuid, poolName, osTemplateId }) =>
      adminResourceService.updateResourceByUuid(
        id,
        vmUuid,
        poolName,
        osTemplateId,
      ),
    retry: false,
    // One by one rather than by the `all` prefix: that prefix also covers the n8n probe keys, and
    // invalidating them would send open panels back to the provider.
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.actions(id) }),
          // The detail page's "Resource Actions" tab reads the same trail under its own filter key.
          queryClient.invalidateQueries({
            queryKey: resourceKeys.actionLists(),
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.attachments(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      ),
  });
}

/**
 * Hook to fetch resource attachments (disks and networks). `enabled` lets the caller withhold the
 * request until it knows the resource is a VM (attachments only make sense there).
 * Returns: ResourceAttachmentsResponse
 */
export function useResourceAttachments(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: resourceKeys.attachments(id),
    queryFn: () => adminResourceService.getResourceAttachments(id),
    enabled: !!id && enabled,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch snapshots for a VM resource. `enabled` lets the caller withhold the request until
 * it knows the resource is a VM (snapshots only exist there).
 * Returns: ResourceSnapshot[]
 */
export function useResourceSnapshots(id: string, enabled: boolean = true) {
  return useQuery<ResourceSnapshot[]>({
    queryKey: resourceKeys.snapshots(id),
    queryFn: () => adminResourceService.getResourceSnapshots(id),
    enabled: !!id && enabled,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch a VM's firewall rules and options. Every read reaches the hypervisor through
 * WiService, so only the firewall panel calls it, and that panel mounts only while the Networking
 * tab is open.
 * Returns: FirewallRulesResponse
 */
export function useResourceFirewall(id: string) {
  return useQuery<FirewallRulesResponse>({
    queryKey: resourceKeys.firewall(id),
    queryFn: () => adminResourceService.getFirewallRules(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch the firewall macro catalog. Cached for the session (`staleTime: Infinity`) like the
 * n8n endpoint catalog — the list is static per deployment. `enabled` withholds it from an operator
 * who cannot write, since only the rule form needs it.
 * Returns: FirewallMacro[]
 */
export function useFirewallMacros(enabled: boolean) {
  return useQuery<FirewallMacro[]>({
    queryKey: resourceKeys.firewallMacros(),
    queryFn: () => adminResourceService.getFirewallMacros(),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * Hook to fetch a VM's recent firewall log lines. `enabled` is always false — the query is only
 * ever run through `refetch()`, when the operator opens the Logs sub-tab.
 * Returns: FirewallLog[]
 */
export function useResourceFirewallLogs(id: string) {
  return useQuery<FirewallLog[]>({
    queryKey: resourceKeys.firewallLogs(id),
    queryFn: () => adminResourceService.getFirewallLogs(id),
    enabled: false,
  });
}

/**
 * Hook to delete a resource (SuperAdmin only).
 *
 * Two outcomes, told apart by the caller's `restorable` (see `isRestorableDeletion`):
 * - A VM with a real machine, deleted at the infrastructure: the backend has asked n8n, and the row
 *   STAYS — it and its disks/networks read Terminating, actions locked, until n8n confirms and it
 *   moves to the pending-purge view. The detail is refetched, not dropped, so the page shows that.
 * - Anything else (record-only, a non-VM): the row is gone when this answers. A VM with no machine
 *   is refused by the full delete (VM_UUID_MISSING); it takes the record delete.
 */
export function useDeleteResource() {
  const queryClient = useQueryClient();

  return useMutation({
    // payload carries the operator's reason and the VM name typed back; for storage/network
    // resources it is an empty body `{}`, which the backend deletes without a confirmation.
    mutationFn: ({
      id,
      payload,
      recordOnly,
    }: {
      id: string;
      payload?: DeleteResourcePayload;
      /** Remove WiCloud's record only, through its own endpoint: a VM, with both answers. */
      recordOnly?: boolean;
      /** The row stays as Terminating after success — see the hook's summary. */
      restorable?: boolean;
    }) => {
      if (recordOnly) {
        // The record delete takes a VM only, and the backend requires both fields.
        if (payload?.reason == null || payload.confirmationName == null) {
          throw new Error(
            "A reason and the VM name are required to remove its record.",
          );
        }

        return adminResourceService.deleteResourceRecord(id, {
          reason: payload.reason,
          confirmationName: payload.confirmationName,
        });
      }

      return adminResourceService.deleteResource(id, payload);
    },
    onSuccess: (_, { id, recordOnly, restorable }) => {
      // Marked stale without a refetch: the cascade may have moved an attached disk's or network's
      // own detail, and those re-read when next opened. Refetching `all` here would re-ask the
      // provider for this VM's metrics and snapshots.
      void queryClient.invalidateQueries({
        queryKey: resourceKeys.all,
        refetchType: "none",
      });

      // Shown at once, so the dialog closes on the new state instead of waiting for a re-read: the
      // row stops offering Delete now, and `onSettled` confirms it in the background.
      showDeletedRow(queryClient, id, restorable === true);

      // The row is still there, now Terminating: `onSettled` re-reads the detail rather than a
      // page that would 404. Nothing is deleted yet — n8n has only accepted the request.
      if (restorable) {
        // A new attempt: its outcome must be reported even if an earlier one already was.
        forgetResourceOutcome(id);
        toast.success("VM deletion started", {
          description:
            "It shows as Terminating, with its actions locked, until the infrastructure confirms. It then reads Decommissioning with a trash icon, restorable for 1 hour.",
        });
        return;
      }

      queryClient.removeQueries({ queryKey: resourceKeys.detail(id) });

      // A record-only deletion is already finished when this runs, so the queued-and-waiting copy
      // would have the operator watching for a job that is never coming. It also carries the one
      // thing they still have to do.
      if (recordOnly) {
        toast.success("WiCloud record removed", {
          description:
            "The machine was left running at the infrastructure and has to be removed there by hand.",
        });
        return;
      }

      toast.success("Resource deletion initiated", {
        description:
          "Deletion has been queued and may take a few minutes to complete.",
      });
    },
    onError: (error: Error) => {
      // DeleteResourceDialog reads these off the same failed mutation: the first two it answers
      // with an inline alert and a switch to record-only, RESOURCE_RUNNING with its shutdown step
      // — a toast on top would repeat what the dialog already explains.
      if (
        error instanceof APIError &&
        (error.isCode("VM_NOT_FOUND_ON_PROVIDER") ||
          error.isCode("VM_UUID_MISSING") ||
          error.isCode("RESOURCE_RUNNING"))
      ) {
        return;
      }

      toast.error("Failed to delete resource", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Not awaited: holding the dialog and the page overlay up for the re-read made the screen jump
    // when they lifted together with a re-rendered table (2026-10-30). `onSuccess` already shows
    // the row's new state; the list and the detail are re-read in the background — on the failure
    // path too, for the header the operator is still looking at.
    onSettled: (_data, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: resourceKeys.detail(id),
        exact: true,
      });
    },
  });
}

/**
 * Shows a deletion's outcome in the cached lists and detail at once: a restorable VM reads
 * Terminating; anything else is gone, so its row leaves the cached pages. The re-read that follows
 * replaces both with the server's answer.
 */
function showDeletedRow(
  queryClient: QueryClient,
  id: string,
  restorable: boolean,
) {
  queryClient.setQueriesData<{ data: Resource[]; pagination: PaginationInfo }>(
    { queryKey: resourceKeys.lists() },
    (page) => {
      if (page == null) {
        return page;
      }

      const rows = restorable
        ? page.data.map((row) =>
            row.id === id ? { ...row, status: "Terminating" as const } : row,
          )
        : page.data.filter((row) => row.id !== id);
      return { ...page, data: rows };
    },
  );

  if (restorable) {
    queryClient.setQueryData<ResourceDetail>(
      resourceKeys.detail(id),
      (detail) =>
        detail == null ? detail : { ...detail, status: "Terminating" },
    );
  }
}

/** What the n8n delete needs to name one VM: its id, its name for the message, and its recorded UUID. */
export interface N8nVmDeleteTarget {
  id: string;
  name: string;
  /** Null or absent when WiCloud has no machine on record, which is itself a refusal. */
  vmUuid?: string | null;
}

/**
 * Hook to delete a VM's machine directly through n8n (SuperAdmin only), leaving WiCloud's record,
 * status and billing untouched. One hook for both entry points — the Expired-VMs row menu and the
 * resource detail page's n8n panel — so the message, the refusal and what is read again afterwards
 * cannot drift apart.
 *
 * A timeout can still have reached the provider, so the VM's infrastructure state is re-read whatever
 * the answer was; it is the read the Expired-VMs table draws its "N8n Infrastructure" column from.
 */
export function useN8nVmDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name, vmUuid }: N8nVmDeleteTarget) => {
      if (!vmUuid) {
        throw new Error(
          `No VM UUID is recorded for ${name}, so n8n cannot be asked to delete it.`,
        );
      }

      return adminResourceService.runN8nDeleteAction(id, vmUuid);
    },
    // No toast: both callers show the outcome inline (an Alert), and a toast would say it twice.
    onSettled: (_result, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: expiredVmKeys.state(id) });
    },
  });
}

/**
 * Hook to reinstall a live VM in place, keeping its IP (SuperAdmin only).
 *
 * A success means the reinstall was queued — the row stays (or becomes) Decommissioning until the
 * rebuild finishes and the status sync brings it back to Running (or Error if it did not boot), so
 * this invalidates the detail and list the same way a power action does, not with an optimistic
 * update.
 */
export function useReinstallResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ReinstallResourcePayload;
    }) => adminResourceService.reinstallResource(id, payload),
    onSuccess: (message, { id }) => {
      // A new attempt: a second reinstall confirmed before a status read ever saw the VM leave
      // Decommissioning from the first one would otherwise still be sitting on that first outcome
      // (e.g. `ReinstallRefused`) and swallow this one's toast and invalidation — see
      // `readResourceOutcome`/`settleResourceOutcome`.
      forgetResourceOutcome(id);

      // The row never leaves the list — it was already Live, now reading Reinstalling.
      toast.success(message, {
        description:
          "Reinstalling — the VM usually reads Running within a few minutes, or Error if it is not running after 10.",
      });
    },
    onError: (error: Error) => {
      // Two codes the backend can refuse a reinstall with are worth their own wording, pointing the
      // operator at what actually fixes them; everything else keeps the backend's own sentence.
      if (error instanceof APIError && error.isCode("RESOURCE_SPEC_MISMATCH")) {
        toast.error(
          "Reinstall refused — stored configuration does not match the infrastructure",
          {
            description:
              'CPU, RAM, disk size or type differ from what the infrastructure reports. Run "Update resource by UUID" first, then reinstall again.',
          },
        );
        return;
      }

      if (
        error instanceof APIError &&
        error.isCode("REINSTALL_NOT_AVAILABLE")
      ) {
        toast.error("Reinstall is not available for this VM", {
          description:
            "The stored configuration is incomplete, or this VM has no pool or provider uuid on record.",
        });
        return;
      }

      toast.error("Failed to start the reinstall", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Same loading standard as the other row writes: awaited so the dialog's spinner and the held
    // menu lift only once the row reads Decommissioning again (or the failure is visible).
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      ),
  });
}

/**
 * Whether "Retry provisioning" may be offered for each of the given resources right now, batched
 * into one call. Open to every admin role — pass only the ids of resources whose status is Error;
 * the query is disabled (nothing fetched) once the list is empty, which is the common case.
 */
export function useProvisioningRetryCheck(errorResourceIds: string[]) {
  const ids = [...new Set(errorResourceIds)].sort();

  return useQuery({
    queryKey: resourceKeys.provisioningRetryCheck(ids),
    queryFn: () => adminResourceService.checkProvisioningRetry(ids),
    enabled: ids.length > 0,
  });
}

/** `check.data?.[resourceId]`, guarding the common "no id" and "check not loaded yet" cases. */
export function getProvisioningRetryCheck(
  data: Record<string, ProvisioningRetryCheck> | undefined,
  resourceId: string | undefined,
): ProvisioningRetryCheck | undefined {
  return resourceId ? data?.[resourceId] : undefined;
}

/**
 * Hook to restart the build of one VM that never ran (SuperAdmin only). The backend refuses every
 * case it cannot retry safely and says why; that sentence is the error toast.
 */
export function useRetryResourceProvisioning() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, recreateMissingVm }: RetryProvisioningRequest) =>
      adminResourceService.retryProvisioning(id, recreateMissingVm),
    onSuccess: (result) => {
      toast.success("Provisioning restarted", {
        description: describeRetryOutcome(result),
      });
    },
    onError: (error: Error) => {
      toast.error("Could not retry provisioning", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Awaited like the other record writes, so the dialog's spinner lifts once the page shows the
    // new status (or the failure is visible).
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
          // Prefix only — the batch check is keyed by the whole (sorted) id list, not a single id.
          queryClient.invalidateQueries({
            queryKey: resourceKeys.provisioningRetryChecks(),
          }),
        ]),
      ),
  });
}

/**
 * Hook to cut the power to several virtual machines in one request (SuperAdmin only).
 *
 * The request succeeds even when every machine in it was refused — the report IS the answer — so a
 * partial batch is never an error here. `onError` is reserved for the request itself failing (403,
 * network), where nothing was stopped at all.
 *
 * The toast says what happened in counts; naming the machines that were refused is the dialog's
 * job, because it has the room to put the provider's own sentence next to each one.
 */
export function useBulkStopResources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkStopResourcesPayload) =>
      adminResourceService.bulkStopResources(payload),
    onSuccess: (result) => {
      if (result.successCount === 0) {
        toast.error("No resources were stopped", {
          description: `All ${result.totalRequested} selected resources were refused. See the list for why.`,
        });
        return;
      }

      if (result.failureCount > 0) {
        toast.warning(
          `${result.successCount} of ${result.totalRequested} resources stopped`,
          {
            description: `${result.failureCount} could not be stopped and were left running.`,
          },
        );
        return;
      }

      toast.success(
        `${result.successCount} resource${result.successCount === 1 ? "" : "s"} stopped`,
      );
    },
    onError: (error: Error) => {
      toast.error("Failed to stop the selected resources", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Invalidated, not removed: a stopped machine still exists and its detail screen must show
    // the new status. `all` is a prefix of lists(), so every cached page refetches in one call.
    onSettled: () =>
      awaitRefresh(
        queryClient.invalidateQueries({ queryKey: resourceKeys.all }),
      ),
  });
}

/**
 * Hook to fill one derived resource column for the rows saved before it existed (SuperAdmin only).
 *
 * Safe to run twice: the backend writes only rows whose column is still empty, so a second run
 * reports zero. No toast: the page that runs it shows each field's outcome next to that field, and
 * several fields run one after another would otherwise stack a toast each.
 */
export function useBackfillResourceField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (field: ResourceBackfillField) =>
      adminResourceService.backfillResourceField(field),
    // The filled column is what the list filters on, so every cached page is stale after a run.
    onSettled: () =>
      awaitRefresh(
        queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
      ),
  });
}

/**
 * Hook to change a resource's expiry date (SuperAdmin only).
 *
 * Only ExpiresAt moves — no billing cycle is extended and nothing is charged, so the expiry scan
 * jobs are what eventually act on the new date.
 */
export function useUpdateResourceExpiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      expiresAt,
      reason,
    }: {
      id: string;
      expiresAt: string;
      reason?: string;
    }) => adminResourceService.updateResourceExpiry(id, expiresAt, reason),
    onSuccess: () => {
      toast.success("Expiry date updated");
    },
    // No error toast: the expiry screen shows the API's message in the panel that saved.
    // Listed one by one rather than by the `all` prefix, as the config-apply mutation above
    // already does: `all` also covers metrics, snapshots, attachments, spec-mismatch and the
    // n8n probes, and the last two are documented as a live round trip to the provider. An
    // expiry edit changes the table row, the detail header ("Expires in Xd") and the audit
    // trail, and nothing else.
    //
    // Billing cycles too: this date is half of the pair the expiry-mismatch report compares, so
    // repairing it here has to drop the machine off that report. Without this the operator fixes
    // a row, presses Check mismatch again and still sees it.
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: billingCycleKeys.all }),
        ]),
      ),
  });
}

/**
 * Hook to save a billing cycle and move the machine's expiry to its end in one write ("Save cycle
 * and expiry", SuperAdmin only). Lives beside `useUpdateResourceExpiry` because it refreshes the
 * same machine queries; the cycle-only save stays `useUpdateBillingCycle`.
 */
export function useUpdateBillingCycleAndExpiry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cycleId,
      payload,
    }: {
      cycleId: string;
      resourceId: string;
      payload: Omit<UpdateBillingCycleRequest, "moveResourceExpiry">;
    }) =>
      adminBillingCycleService.updateBillingCycle(cycleId, {
        ...payload,
        moveResourceExpiry: true,
      }),
    onSuccess: () => {
      toast.success("Billing cycle and expiry updated");
    },
    // No error toast: the expiry screen shows the API's message in the panel that saved.
    // Same set as useUpdateResourceExpiry: both dates of the pair moved.
    onSettled: (_data, _error, { resourceId }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(resourceId),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(resourceId),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: billingCycleKeys.all }),
        ]),
      ),
  });
}

/**
 * Hook to write a new IP address into a VM's cloud-init configuration at the provider
 * (SuperAdmin only).
 *
 * Unlike `useUpdateResourcePublicIp`, this one changes the machine. It does **not** restart it, so
 * the VM keeps answering on its old address until someone does — the toast says so, because an
 * operator who assumes otherwise will report the address as changed while the customer is still on
 * the old one.
 */
export function useApplyVmIpAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ApplyVmIpAddressPayload;
    }) => adminResourceService.applyVmIpAddress(id, payload),
    onSuccess: (result, { id }) => {
      // Not awaited, and deliberately the one write here that does not wait for its refetch. The
      // answer can carry the only readable copy of a generated password, and React Query hands the
      // caller `data` only once every callback here has settled — so waiting would keep that password
      // behind a re-read of rows the result view does not even show. The dialog stays on the result
      // either way, and the header catches up behind it.
      //
      // Same reasoning as the two mutations above: the `all` prefix would drag metrics, snapshots
      // and the n8n probes along, and those are live provider round trips. Every row the record
      // update touched is invalidated, since each has its own detail page.
      const writtenIds = new Set([
        id,
        ...result.recordChanges.map((c) => c.resourceId),
      ]);
      for (const writtenId of writtenIds) {
        queryClient.invalidateQueries({
          queryKey: resourceKeys.detail(writtenId),
          exact: true,
        });
      }

      queryClient.invalidateQueries({ queryKey: resourceKeys.activityLog(id) });
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });

      // The restart is the one thing the operator has to act on, so it leads every message here.
      if (result.alreadyConfigured) {
        toast.info(`Already configured for ${result.ipAddress}`, {
          description:
            "Nothing was sent. The VM still needs a restart for this address to take effect.",
        });
      } else {
        toast.success(`VM configured for ${result.ipAddress}`, {
          description:
            "Restart the VM for it to take effect — it is still reachable on the old address until then.",
        });
      }

      // Its own toast, and an error one: half the operation the operator asked for did not happen,
      // and the dialog keeps the address result on screen either way.
      if (result.passwordResetError) {
        toast.error("The password was not reset", {
          description: result.passwordResetError,
        });
      }

      // Same reasoning: the machine was reconfigured but WiCloud still shows the old address, and
      // nothing else on screen would say so.
      if (result.recordUpdateError) {
        toast.error("WiCloud's record was not updated", {
          description: result.recordUpdateError,
        });
      }
    },
    onError: (error: Error) => {
      toast.error("Failed to change the VM's IP address", {
        description: error.message || "An unexpected error occurred",
      });
    },
  });
}

/**
 * Hook to correct the public IP address WiCloud has on record for a VM (SuperAdmin only).
 *
 * Writes WiCloud's own record only — nothing is asked of the provider, so no address is reassigned
 * on the hypervisor and no DNS record is rewritten. The backend carries the correction across every
 * place an address lives behind that VM's UUID and answers with the rows it actually wrote; rows
 * already holding the address are left alone, which is why an empty `changes` reports a no-op
 * rather than a success.
 */
export function useUpdateResourcePublicIp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ipAddress,
      reason,
    }: {
      id: string;
      ipAddress: string;
      reason?: string;
    }) => adminResourceService.updateResourcePublicIp(id, ipAddress, reason),
    onSuccess: (result) => {
      if (result.changes.length === 0) {
        // Reported as-is rather than as a success: the operator asked for a change and got none,
        // and silently showing "IP address updated" would hide that every row was already there.
        toast.info(`Already ${result.ipAddress}`, {
          description: "No row needed changing.",
        });
        return;
      }

      const attached = result.changes.length - 1;

      // The five-minute window is the part an operator cannot see coming, so it is named rather
      // than left to be discovered when the address reverts.
      const description = result.willBeOverwrittenBySync
        ? "This VM is still reconciled against the provider every 5 minutes — if the provider reports a different address, it will be written back."
        : attached > 0
          ? `${result.changes.length} rows updated, including ${attached} attached.`
          : undefined;

      toast.success(`Public IP set to ${result.ipAddress}`, { description });
    },
    onError: (error: Error) => {
      toast.error("Failed to update the IP address", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Same reasoning as the status mutation: the `all` prefix would drag metrics, snapshots and
    // the n8n probes along, and those are live provider round trips. An address correction shows
    // up in the table row, the detail header, the Network tab and the audit trail, and nowhere
    // else.
    //
    // Every written row is invalidated, not just the one that was addressed: the correction
    // carries the attached network rows too, and each of those has its own detail page.
    onSettled: (result, _error, { id }) => {
      const writtenIds = new Set([
        id,
        ...(result?.changes ?? []).map((c) => c.resourceId),
      ]);
      return awaitRefresh(
        Promise.all([
          ...[...writtenIds].map((writtenId) =>
            queryClient.invalidateQueries({
              queryKey: resourceKeys.detail(writtenId),
              exact: true,
            }),
          ),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      );
    },
  });
}

/**
 * Hook to correct the status WiCloud has on record for a resource (SuperAdmin only).
 *
 * Writes Status only — nothing is asked of the provider, so this does not start, stop or suspend
 * anything. The backend cascades to the disk and network rows the resource solely owns, and
 * answers with the rows it actually moved; rows already at the requested status are left alone,
 * which is why an empty `changes` reports a no-op rather than a success.
 */
export function useUpdateResourceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ResourceStatus }) =>
      adminResourceService.updateResourceStatus(id, status),
    onSuccess: (result) => {
      if (result.changes.length === 0) {
        // Reported as-is rather than as a success: the operator asked for a change and got none,
        // and silently showing "Status updated" would hide that the row was already there.
        toast.info(`Already ${result.status}`, {
          description: "No resource needed changing.",
        });
        return;
      }

      // The cascade is the part an operator cannot see coming, so the count names it explicitly.
      const cascaded = result.changes.length - 1;
      toast.success(`Status set to ${result.status}`, {
        description:
          cascaded > 0
            ? `${result.changes.length} resources updated, including ${cascaded} attached.`
            : undefined,
      });
    },
    onError: (error: Error) => {
      toast.error("Failed to update status", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // Same reasoning as the expiry mutation: the `all` prefix would drag metrics, snapshots and
    // the n8n probes along, and those are live provider round trips. A status correction shows
    // up in the table row, the detail header and the audit trail, and nowhere else.
    //
    // Every moved row is invalidated, not just the one that was addressed: the cascade restatuses
    // the attached disks and networks too, and each of those has its own detail page.
    onSettled: (result, _error, { id }) => {
      const movedIds = new Set([
        id,
        ...(result?.changes ?? []).map((c) => c.resourceId),
      ]);
      return awaitRefresh(
        Promise.all([
          ...[...movedIds].map((movedId) =>
            queryClient.invalidateQueries({
              queryKey: resourceKeys.detail(movedId),
              exact: true,
            }),
          ),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      );
    },
  });
}

/**
 * Hook to run one VM power action (SuperAdmin only).
 *
 * A success means the provider accepted the action. Start, Stop, Shutdown, Suspend and Resume write
 * the new status immediately (AdminResourcePowerActionCommandHandler); Restart and Reset leave it
 * as it was. The invalidate, rather than an optimistic update, shows what the server wrote.
 */
export function useResourcePowerAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: ResourcePowerAction;
      reason?: string;
    }) => adminResourceService.runPowerAction(id, action, reason),
    onSuccess: (_, { action }) => {
      toast.success(`${RESOURCE_POWER_ACTION_META[action].label} initiated`);
    },
    onError: (error: Error, { action }) => {
      toast.error(
        `Failed to ${RESOURCE_POWER_ACTION_META[action].label.toLowerCase()} resource`,
        { description: error.message || "An unexpected error occurred" },
      );
    },
    // Same reason as the expiry mutation: the `all` prefix would drag metrics, snapshots,
    // attachments, spec-mismatch and the n8n probes along with it, and a power action changes
    // none of those. It does add a row to the resource_actions trail, so that key is included
    // here where the expiry edit leaves it out.
    //
    // Returned from onSettled — see the loading-standard note above the writes: the menu spinner and the
    // held-open confirm lift only once the row and header show the new status. On failure too: a
    // provider timeout does not prove the machine was left alone.
    onSettled: (_data, _error, { id }) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: resourceKeys.activityLog(id),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.actions(id) }),
          // The detail page's "Resource Actions" tab reads the same trail under its own filter key.
          queryClient.invalidateQueries({
            queryKey: resourceKeys.actionLists(),
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      ),
  });
}

/**
 * Restores a deleted VM (SuperAdmin). The API only queues the rebuild, so the row leaves the deleted
 * views at once and turns Running later, which the status watch and the next reads pick up.
 */
export function useRestoreDeletedResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminResourceService.restore(id),
    onSuccess: (message: string) => {
      toast.success(message);
    },
    onError: (error: Error) => {
      // The API's own sentence: the window closed, or the name was taken.
      toast.error("Failed to restore the VM", {
        description: error.message || "An unexpected error occurred",
      });
    },
    // The detail page renders the same menu: without a fresh detail read it keeps offering "Restore VM…"
    // on a row that is already restoring, and a second click answers RESOURCE_NOT_FOUND.
    onSettled: (_data, _error, id) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: resourceKeys.detail(id),
            exact: true,
          }),
          queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
        ]),
      ),
  });
}

/**
 * Sends one machine's owner an expiry reminder covering that machine alone.
 *
 * Every refusal — wrong type, no term, beyond the window, no VM UUID on record — arrives
 * as a 409 whose message names what is in the way, so the toast shows the API's sentence rather
 * than a generic failure the operator would have to guess at.
 */
/** How long after starting a finalize the lists are read again: the job runs on the Worker. */
const FINALIZE_REFRESH_DELAY_MS = 5000;

/**
 * Starts the finalize of a deleted VM by hand (SuperAdmin) — for a row whose scheduled finalize never
 * ran. The API only enqueues the job, so the lists are read once now and once more a few seconds
 * later, when the row has normally turned Terminated.
 */
export function useFinalizeResourceDeletion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminResourceService.finalizeDeletion(id),
    onSuccess: (message: string) => {
      toast.success(message);
      window.setTimeout(
        () =>
          void queryClient.invalidateQueries({
            queryKey: resourceKeys.lists(),
          }),
        FINALIZE_REFRESH_DELAY_MS,
      );
    },
    onError: (error: Error) => {
      // The API's own sentence: "kept until …", "not waiting to be purged".
      toast.error("Failed to finalize the deletion", {
        description: error.message || "An unexpected error occurred",
      });
    },
    onSettled: () =>
      awaitRefresh(
        queryClient.invalidateQueries({ queryKey: resourceKeys.lists() }),
      ),
  });
}

export function useSendResourceExpiryReminder() {
  return useMutation({
    mutationFn: (resourceId: string) =>
      adminResourceService.sendExpiryReminder(resourceId),
    onSuccess: (result) => {
      toast.success(result.message);
    },
    onError: (error: Error) => {
      // The API client's interceptor already rejects with an APIError carrying the server's own
      // message, so this is the 409's sentence — "beyond the 7-day reminder window" and the rest —
      // not a bare status.
      toast.error(error.message || "Failed to send the expiry reminder");
    },
  });
}

/**
 * Opens a VM's console in a new tab — the client app's VMDetailHero console flow.
 *
 * Call `openConsole` straight from the click handler: the tab is opened inside the click, before the
 * request, because a tab opened once the link comes back is no longer tied to the operator's gesture
 * and popup blockers refuse it. The blank tab is cut off from this page and says what it waits for.
 */
export function useOpenResourceConsole(resourceId: string) {
  const mutation = useMutation({
    // The tab is the mutation's variable, so onSuccess/onError can point or close that same tab.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- declared only to type that variable
    mutationFn: (_tab: Window | null) =>
      adminResourceService.getConsole(resourceId),
    onSuccess: (data, tab) => {
      if (tab && !tab.closed) {
        tab.location.replace(data.consoleUrl);
      } else {
        // The browser refused even the click-time tab; this still works wherever popups are allowed.
        window.open(data.consoleUrl, "_blank", "noopener,noreferrer");
      }
      // The client's own copy, so both consoles say the same thing.
      toast.success("Console opened", {
        description: `Link expires in ${data.linkExpiresInSeconds}s (session ${data.userExpiresInMinutes}m)`,
      });
    },
    onError: (error: unknown, tab) => {
      tab?.close();
      toast.error("Could not open console", {
        description:
          error instanceof Error
            ? error.message
            : "Failed to open the console.",
      });
    },
  });

  const openConsole = () => {
    const tab = window.open("", "_blank");
    if (tab) {
      tab.opener = null;
      const referrer = tab.document.createElement("meta");
      referrer.name = "referrer";
      referrer.content = "no-referrer";
      tab.document.head.appendChild(referrer);
      tab.document.title = "Opening…";
      tab.document.body.textContent = "Opening…";
    }
    mutation.mutate(tab);
  };

  return { openConsole, isPending: mutation.isPending };
}

/** Its own key root: a bulk stop invalidates `resources`, and this list must not refetch with it. */
export const expiredVmKeys = {
  all: ["expired-vms"] as const,
  list: (filters: ExpiredVmFilters) =>
    [...expiredVmKeys.all, "list", filters] as const,
  counts: () => [...expiredVmKeys.all, "counts"] as const,
  orphans: (page: number, filters: OrphanChildrenFilters) =>
    [...expiredVmKeys.all, "orphans", page, filters] as const,
  // Outside `all`: refreshing the list and counts must not refetch the slow provider read.
  statesRoot: ["expired-vm-states"] as const,
  state: (id: string) => [...expiredVmKeys.statesRoot, id] as const,
};

/**
 * Refresh after rows were deleted or removed: the list and counts refetch, and the Resources pages
 * (inactive here) go stale. The provider states only go stale: removing the query the table still
 * observes would refetch n8n for the old ids, so the new ids fetch once when the list lands.
 *
 * Called by the two bulk writes themselves, the moment the backend answers — see `useDeleteExpiredVms`.
 */
export function useRefreshExpiredVms() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: expiredVmKeys.all });
    queryClient.invalidateQueries({
      queryKey: expiredVmKeys.statesRoot,
      refetchType: "none",
    });
    queryClient.invalidateQueries({ queryKey: resourceKeys.all });
  };
}

/**
 * Hook for one page of the expired-VM clean-up list (SuperAdmin only).
 *
 * The previous page stays on screen while the next loads, so the table does not flash.
 */
export function useExpiredVms(filters: ExpiredVmFilters, enabled = true) {
  return useQuery({
    enabled,
    queryKey: expiredVmKeys.list(filters),
    queryFn: ({ signal }) =>
      adminResourceService.getExpiredVms(filters, signal),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/**
 * Hook for the infrastructure state of the VMs on the visible page (SuperAdmin only).
 *
 * One request per VM, all at once, after the list has its rows: each row fills in as its own answer
 * arrives instead of waiting for the slowest. Never retries: a failed row stays blank until Re-check.
 * A row that stays on the next page keeps its cached answer for 30 seconds.
 */
export function useProviderStates(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: expiredVmKeys.state(id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        adminResourceService.getProviderState(id, signal),
      staleTime: 30_000,
      retry: false,
    })),
    combine: (results) => combineProviderStates(ids, results),
  });
}

/** The page's answers so far: the states by id, the rows still waiting, and the first failure. */
function combineProviderStates(
  ids: string[],
  results: UseQueryResult<ResourceProviderState | null>[],
) {
  const data: Record<string, ResourceProviderState> = {};
  const pendingIds: string[] = [];
  let isFetching = false;
  let error: Error | null = null;
  // A plain loop, not forEach: TypeScript does not see assignments made inside a callback.
  for (let index = 0; index < results.length; index++) {
    const result = results[index];
    const id = ids[index];
    if (result.data != null) {
      data[id] = result.data;
    }
    if (result.isPending) {
      pendingIds.push(id);
    }
    if (result.isFetching) {
      isFetching = true;
    }
    if (error == null && result.error != null) {
      error = result.error;
    }
  }
  return { data, pendingIds, isFetching, error };
}

/**
 * Hook to delete expired VMs, or only their records (SuperAdmin only).
 *
 * The list refreshes as soon as the backend answers, not when the report dialog closes: a batch runs
 * for minutes, one VM after another, and the rows it started have to show as `Terminating` — and be
 * watched — from that moment. The dialog holds the request's own report, which a list refresh behind
 * its overlay cannot disturb.
 */
export function useDeleteExpiredVms() {
  const refresh = useRefreshExpiredVms();

  return useMutation({
    mutationFn: ({
      payload,
      recordOnly,
    }: {
      payload: DeleteExpiredVmsPayload;
      recordOnly: boolean;
    }) => adminResourceService.deleteExpiredVms(payload, recordOnly),
    onSuccess: (result, { recordOnly }) => {
      // A full delete only starts here (Terminating → job → kept restorable); a record-only one is done.
      const outcome = recordOnly ? "deleted" : "queued for deletion";

      if (result.successCount === 0) {
        toast.error(`No VMs were ${outcome}`, {
          description: `All ${result.totalRequested} selected VMs failed. See the report for why.`,
        });
        return;
      }

      if (result.failureCount > 0) {
        toast.warning(
          `${result.successCount} of ${result.totalRequested} VMs ${outcome}`,
          {
            description: `${result.failureCount} failed and were left as they were.`,
          },
        );
        return;
      }

      toast.success(
        `${result.successCount} VM${result.successCount === 1 ? "" : "s"} ${outcome}`,
      );
    },
    // No error toast: a request that fails has no per-row report, so the dialog that started it shows
    // the backend's sentence in place (`ExpiredVmsFailure`) instead of a toast that disappears.
    //
    // Deliberate exception to the loading standard above: here the spinner is the "request still
    // running" state inside the dialog, so awaiting the re-read would leave "Deleting… this can take a
    // few minutes" on screen after the backend had already answered. The list catches up behind the
    // dialog's overlay while the report is read. It runs on failure too, since rows may have been
    // deleted before it failed.
    onSettled: () => {
      refresh();
    },
  });
}

/** Row counts for the tab pills (SuperAdmin only); refetched with the rest of `expiredVmKeys.all`. */
export function useExpiredVmCounts() {
  return useQuery({
    queryKey: expiredVmKeys.counts(),
    queryFn: ({ signal }) => adminResourceService.getExpiredVmCounts(signal),
    staleTime: 30_000,
  });
}

export const ORPHAN_PAGE_SIZE = 20;

/** Hook for one page of orphaned disks and networks; fetches only while the Orphans tab is open. */
export function useOrphanChildren(
  page: number,
  filters: OrphanChildrenFilters,
  enabled: boolean,
) {
  return useQuery({
    queryKey: expiredVmKeys.orphans(page, filters),
    queryFn: ({ signal }) =>
      adminResourceService.getOrphanChildren(
        page,
        ORPHAN_PAGE_SIZE,
        filters,
        signal,
      ),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/**
 * Hook to remove orphaned disks and networks from WiCloud's records (SuperAdmin only). The list
 * refreshes as soon as the backend answers, like the expired-VM deletion above.
 */
export function useRemoveOrphanChildren() {
  const refresh = useRefreshExpiredVms();

  return useMutation({
    mutationFn: (payload: RemoveOrphanChildrenPayload) =>
      adminResourceService.removeOrphanChildren(payload),
    onSuccess: (result) => {
      if (result.successCount === 0) {
        toast.error("No records were removed", {
          description: `All ${result.totalRequested} selected rows failed. See the report for why.`,
        });
        return;
      }

      if (result.failureCount > 0) {
        toast.warning(
          `${result.successCount} of ${result.totalRequested} records removed`,
          {
            description: `${result.failureCount} failed and were left as they were.`,
          },
        );
        return;
      }

      toast.success(
        `${result.successCount} record${result.successCount === 1 ? "" : "s"} removed`,
      );
    },
    // No error toast and not awaited, for the same reasons as the expired-VM deletion above: the
    // dialog shows the request's own failure, and its report is the answer to the operator's click.
    onSettled: () => {
      refresh();
    },
  });
}
